import { ArrowDown, ArrowUp, ChevronsUpDown } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { TableHead } from "@/components/ui/table"
import { cn } from "@/lib/utils"

type SortDirection = "asc" | "desc" | null

interface SortableTableHeaderProps<T extends string> {
  children: React.ReactNode
  sortKey: T
  currentSort: { key: T | null; direction: SortDirection }
  onSort: (key: T) => void
  className?: string
  align?: "left" | "right" | "center"
}

export function SortableTableHeader<T extends string>({
  children,
  sortKey,
  currentSort,
  onSort,
  className,
  align = "left",
}: SortableTableHeaderProps<T>) {
  const isActive = currentSort.key === sortKey
  const direction = isActive ? currentSort.direction : null

  const handleClick = () => {
    onSort(sortKey)
  }

  const getSortIcon = () => {
    if (direction === "asc") {
      return <ArrowUp className="ml-1 size-4" />
    }
    if (direction === "desc") {
      return <ArrowDown className="ml-1 size-4" />
    }
    return <ChevronsUpDown className="ml-1 size-4 opacity-40" />
  }

  const alignClass = {
    left: "text-left",
    right: "text-right",
    center: "text-center",
  }[align]

  return (
    <TableHead className={cn(alignClass, "p-0", className)}>
      <Button
        variant="ghost"
        className={cn(
          "h-12 w-full justify-start px-4 text-xs font-medium tracking-wider uppercase hover:bg-transparent",
          isActive && "text-foreground",
          align === "right" && "justify-end",
          align === "center" && "justify-center",
          !isActive && "text-muted-foreground"
        )}
        onClick={handleClick}
      >
        <span className="flex items-center gap-1 whitespace-nowrap">
          {children}
          {getSortIcon()}
        </span>
      </Button>
    </TableHead>
  )
}
